'use client';

import { createClient } from '@/lib/supabase/client';
import { PrintJob, ResinLot, Patient } from '@/lib/mockData';

function isSchemaError(error: { code?: string; message?: string }): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(error.message!));
  }
  return false;
}

// ─── PRINT JOBS ────────────────────────────────────────────────────────────

export const printJobService = {
  async getAll(): Promise<PrintJob[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('print_jobs')
        .select(`
          *,
          patients(id, name, external_id),
          resin_lots(id, lot_number, material_type, material_brand, bottle_sn, opened_at, expires_at, is_active)
        `)
        .order('printed_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Print jobs fetch error:', error.message);
        return [];
      }

      return (data || []).map(mapPrintJob);
    } catch (e: unknown) {
      console.log('Print jobs error:', (e as Error).message);
      return [];
    }
  },

  async getById(id: string): Promise<PrintJob | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('print_jobs')
        .select(`
          *,
          patients(id, name, external_id),
          resin_lots(id, lot_number, material_type, material_brand, bottle_sn, opened_at, expires_at, is_active)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? mapPrintJob(data) : null;
    } catch (e: unknown) {
      console.log('Print job fetch error:', (e as Error).message);
      return null;
    }
  },

  async updateClinical(id: string, updates: Partial<PrintJob>): Promise<boolean> {
    const supabase = createClient();
    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.patient_id !== undefined) dbUpdates.patient_id = updates.patient_id || null;
      if (updates.resin_lot_id !== undefined) dbUpdates.resin_lot_id = updates.resin_lot_id || null;
      if (updates.clinical_type !== undefined) dbUpdates.clinical_type = updates.clinical_type || null;
      if (updates.tooth !== undefined) dbUpdates.tooth = updates.tooth || null;
      if (updates.indication !== undefined) dbUpdates.indication = updates.indication || null;
      if (updates.practitioner !== undefined) dbUpdates.practitioner = updates.practitioner || null;
      if (updates.operator_name !== undefined) dbUpdates.operator_name = updates.operator_name || null;
      if (updates.wash_done !== undefined) dbUpdates.wash_done = updates.wash_done;
      if (updates.wash_machine !== undefined) dbUpdates.wash_machine = updates.wash_machine || null;
      if (updates.wash_duration_min !== undefined) dbUpdates.wash_duration_min = updates.wash_duration_min;
      if (updates.cure_done !== undefined) dbUpdates.cure_done = updates.cure_done;
      if (updates.cure_machine !== undefined) dbUpdates.cure_machine = updates.cure_machine || null;
      if (updates.cure_duration_min !== undefined) dbUpdates.cure_duration_min = updates.cure_duration_min;
      if (updates.qc_status !== undefined) dbUpdates.qc_status = updates.qc_status;
      if (updates.validated_by !== undefined) dbUpdates.validated_by = updates.validated_by || null;
      if (updates.validated_at !== undefined) dbUpdates.validated_at = updates.validated_at || null;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes || null;

      const { error } = await supabase
        .from('print_jobs')
        .update(dbUpdates)
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Update error:', error.message);
        return false;
      }
      return true;
    } catch (e: unknown) {
      console.log('Update clinical error:', (e as Error).message);
      return false;
    }
  },

  async importJobs(jobs: Array<Record<string, unknown>>): Promise<{ imported: number; duplicates: number; errors: number }> {
    const supabase = createClient();
    let imported = 0;
    let duplicates = 0;
    let errors = 0;

    for (const item of jobs) {
      try {
        const shiningId = String(item.id ?? '');
        const printedAt = item.changeOn
          ? new Date(String(item.changeOn).replace(' ', 'T')).toISOString()
          : new Date().toISOString();

        const { error } = await supabase.from('print_jobs').insert({
          shining_job_id: shiningId,
          printer_sn: String(item.sn ?? ''),
          file_name: String(item.fileName ?? ''),
          bucket: String(item.bucket ?? ''),
          dfs_id: String(item.dfsID ?? ''),
          material_brand: String(item.materialBrand ?? ''),
          material_type: String(item.materialType ?? ''),
          consumption_ml: parseFloat(String(item.consumption ?? '0')),
          print_time_sec: Number(item.printTime ?? 0),
          layer_thickness: Number(item.layerThickness ?? 0),
          total_layer: Number(item.totalLayer ?? 0),
          status: String(item.status ?? 'success'),
          printed_at: printedAt,
          created_by: String(item.createBy ?? ''),
          metrail_box_sn: String(item.metrailBoxSn ?? ''),
          task_origin: String(item.TaskOrigin ?? ''),
          raw_json: item,
          imported_at: new Date().toISOString(),
        });

        if (error) {
          if (error.code === '23505') {
            duplicates++;
          } else {
            errors++;
          }
        } else {
          imported++;
        }
      } catch {
        errors++;
      }
    }

    return { imported, duplicates, errors };
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('print_jobs').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: unknown) {
      console.log('Delete error:', (e as Error).message);
      return false;
    }
  },

  async getExistingShiningIds(): Promise<Set<string>> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('print_jobs')
        .select('shining_job_id');
      if (error) return new Set();
      return new Set((data || []).map((r: { shining_job_id: string }) => r.shining_job_id));
    } catch {
      return new Set();
    }
  },

  async getKPIs() {
    const supabase = createClient();
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const { data, error } = await supabase
        .from('print_jobs')
        .select('consumption_ml, print_time_sec, patient_id, qc_status, status, printed_at');

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      const jobs = data || [];
      const totalJobs = jobs.length;
      const monthJobs = jobs.filter((j: { printed_at: string }) => j.printed_at >= startOfMonth).length;
      const totalConsumption = jobs.reduce((s: number, j: { consumption_ml: number }) => s + (j.consumption_ml || 0), 0);
      const totalTimeSec = jobs.reduce((s: number, j: { print_time_sec: number }) => s + (j.print_time_sec || 0), 0);
      const unlinkedPatient = jobs.filter((j: { patient_id: string | null }) => !j.patient_id).length;
      const unvalidated = jobs.filter((j: { qc_status: string }) => j.qc_status === 'pending').length;
      const failed = jobs.filter((j: { status: string }) => j.status === 'failed').length;
      const failureRate = totalJobs > 0 ? (failed / totalJobs) * 100 : 0;
      const estimatedCost = totalConsumption * 0.08;

      return { totalJobs, monthJobs, totalConsumption, totalTimeSec, unlinkedPatient, unvalidated, failureRate, estimatedCost, failed };
    } catch (e: unknown) {
      console.log('KPIs error:', (e as Error).message);
      return null;
    }
  },

  async getChartData() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('print_jobs')
        .select('consumption_ml, printed_at, material_type')
        .order('printed_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return { consumptionData: [], materialData: [] };
      }

      const jobs = data || [];

      // Last 14 days consumption
      const today = new Date();
      const days: Array<{ date: string; ml: number; jobs: number }> = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().split('T')[0];
        const dayJobs = jobs.filter((j: { printed_at: string }) => j.printed_at?.startsWith(dateStr));
        days.push({
          date: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
          ml: dayJobs.reduce((s: number, j: { consumption_ml: number }) => s + (j.consumption_ml || 0), 0),
          jobs: dayJobs.length,
        });
      }

      // By material type
      const materialMap: Record<string, { jobs: number; ml: number }> = {};
      jobs.forEach((j: { material_type: string; consumption_ml: number }) => {
        const mat = j.material_type || 'Inconnu';
        if (!materialMap[mat]) materialMap[mat] = { jobs: 0, ml: 0 };
        materialMap[mat].jobs++;
        materialMap[mat].ml += j.consumption_ml || 0;
      });
      const materialData = Object.entries(materialMap).map(([material, v]) => ({
        material,
        jobs: v.jobs,
        ml: v.ml,
      }));

      return { consumptionData: days, materialData };
    } catch (e: unknown) {
      console.log('Chart data error:', (e as Error).message);
      return { consumptionData: [], materialData: [] };
    }
  },

  async getIncomplete(): Promise<PrintJob[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('print_jobs')
        .select(`*, patients(id, name)`)
        .or('patient_id.is.null,resin_lot_id.is.null,qc_status.eq.pending')
        .order('printed_at', { ascending: false })
        .limit(5);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(mapPrintJob);
    } catch (e: unknown) {
      console.log('Incomplete jobs error:', (e as Error).message);
      return [];
    }
  },

  async getRecentActivity() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('print_jobs')
        .select('id, file_name, status, qc_status, validated_by, validated_at, imported_at, printed_at, patients(name)')
        .order('imported_at', { ascending: false })
        .limit(20);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      // Sort by most recent activity: validated_at or imported_at, whichever is latest
      const rows = (data || []).map((row) => ({
        ...row,
        _sortDate: row.validated_at && row.validated_at > (row.imported_at ?? '')
          ? row.validated_at
          : (row.imported_at ?? ''),
      }));
      rows.sort((a, b) => b._sortDate.localeCompare(a._sortDate));

      return rows.slice(0, 6);
    } catch (e: unknown) {
      console.log('Activity error:', (e as Error).message);
      return [];
    }
  },
};

// ─── RESIN LOTS ────────────────────────────────────────────────────────────

export const resinLotService = {
  async getAll(): Promise<ResinLot[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('resin_lots')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(mapResinLot);
    } catch (e: unknown) {
      console.log('Resin lots error:', (e as Error).message);
      return [];
    }
  },

  async create(lot: Partial<ResinLot>): Promise<ResinLot | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('resin_lots')
        .insert({
          material_brand: lot.material_brand,
          material_type: lot.material_type,
          lot_number: lot.lot_number,
          bottle_sn: lot.bottle_sn || null,
          opened_at: lot.opened_at || null,
          expires_at: lot.expires_at || null,
          is_active: lot.is_active ?? false,
          notes: lot.notes || null,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapResinLot(data);
    } catch (e: unknown) {
      console.log('Create lot error:', (e as Error).message);
      return null;
    }
  },

  async update(id: string, lot: Partial<ResinLot>): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('resin_lots')
        .update({
          material_brand: lot.material_brand,
          material_type: lot.material_type,
          lot_number: lot.lot_number,
          bottle_sn: lot.bottle_sn || null,
          opened_at: lot.opened_at || null,
          expires_at: lot.expires_at || null,
          is_active: lot.is_active,
          notes: lot.notes || null,
        })
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: unknown) {
      console.log('Update lot error:', (e as Error).message);
      return false;
    }
  },

  async setActive(id: string, materialType: string): Promise<boolean> {
    const supabase = createClient();
    try {
      // Deactivate all lots of same material type
      await supabase
        .from('resin_lots')
        .update({ is_active: false })
        .eq('material_type', materialType);

      // Activate the selected one
      const { error } = await supabase
        .from('resin_lots')
        .update({ is_active: true })
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: unknown) {
      console.log('Set active error:', (e as Error).message);
      return false;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('resin_lots').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: unknown) {
      console.log('Delete lot error:', (e as Error).message);
      return false;
    }
  },
};

// ─── PATIENTS ──────────────────────────────────────────────────────────────

export const patientService = {
  async getAll(): Promise<Patient[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: unknown) {
      console.log('Patients error:', (e as Error).message);
      return [];
    }
  },

  async create(name: string, externalId?: string): Promise<Patient | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('patients')
        .insert({ name, external_id: externalId || null })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (e: unknown) {
      console.log('Create patient error:', (e as Error).message);
      return null;
    }
  },
};

// ─── MAPPERS ───────────────────────────────────────────────────────────────

function mapPrintJob(row: Record<string, unknown>): PrintJob {
  const patient = row.patients as Record<string, unknown> | null;
  const lot = row.resin_lots as Record<string, unknown> | null;
  return {
    id: String(row.id ?? ''),
    shining_job_id: String(row.shining_job_id ?? ''),
    printer_sn: String(row.printer_sn ?? ''),
    file_name: String(row.file_name ?? ''),
    bucket: String(row.bucket ?? ''),
    dfs_id: String(row.dfs_id ?? ''),
    preview_url: row.preview_url ? String(row.preview_url) : null,
    material_brand: String(row.material_brand ?? ''),
    material_type: String(row.material_type ?? ''),
    consumption_ml: Number(row.consumption_ml ?? 0),
    print_time_sec: Number(row.print_time_sec ?? 0),
    layer_thickness: Number(row.layer_thickness ?? 0),
    total_layer: Number(row.total_layer ?? 0),
    status: (row.status as PrintJob['status']) ?? 'success',
    printed_at: String(row.printed_at ?? ''),
    created_by: String(row.created_by ?? ''),
    metrail_box_sn: String(row.metrail_box_sn ?? ''),
    task_origin: String(row.task_origin ?? ''),
    raw_json: (row.raw_json as object) ?? {},
    imported_at: String(row.imported_at ?? ''),
    patient_id: patient ? String(patient.id) : null,
    patient_name: patient ? String(patient.name) : null,
    resin_lot_id: lot ? String(lot.id) : null,
    resin_lot_number: lot ? String(lot.lot_number) : null,
    clinical_type: row.clinical_type ? String(row.clinical_type) : null,
    tooth: row.tooth ? String(row.tooth) : null,
    indication: row.indication ? String(row.indication) : null,
    practitioner: row.practitioner ? String(row.practitioner) : null,
    operator_name: row.operator_name ? String(row.operator_name) : null,
    wash_done: Boolean(row.wash_done ?? false),
    wash_machine: row.wash_machine ? String(row.wash_machine) : null,
    wash_duration_min: row.wash_duration_min ? Number(row.wash_duration_min) : null,
    cure_done: Boolean(row.cure_done ?? false),
    cure_machine: row.cure_machine ? String(row.cure_machine) : null,
    cure_duration_min: row.cure_duration_min ? Number(row.cure_duration_min) : null,
    qc_status: (row.qc_status as PrintJob['qc_status']) ?? 'pending',
    validated_by: row.validated_by ? String(row.validated_by) : null,
    validated_at: row.validated_at ? String(row.validated_at) : null,
    notes: row.notes ? String(row.notes) : null,
  };
}

function mapResinLot(row: Record<string, unknown>): ResinLot {
  return {
    id: String(row.id ?? ''),
    material_brand: String(row.material_brand ?? ''),
    material_type: String(row.material_type ?? ''),
    lot_number: String(row.lot_number ?? ''),
    bottle_sn: row.bottle_sn ? String(row.bottle_sn) : null,
    opened_at: row.opened_at ? String(row.opened_at) : null,
    expires_at: row.expires_at ? String(row.expires_at) : null,
    is_active: Boolean(row.is_active ?? false),
    notes: row.notes ? String(row.notes) : null,
    created_at: String(row.created_at ?? ''),
  };
}
